import React, { useMemo } from 'react';
import {
  CButton,
  CCallout,
  CCard,
  CCardBody,
  CCardHeader,
  CCardTitle,
  CCol,
  CContainer,
  CLink,
  CRow,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import _t from 'counterpart';
import { getAffiliateWalletWithTransactions, getCommissionBalance } from '../services/BackendService';
import PaginationTable from '../components/PaginationTable';
import { useQuery } from 'react-query';
import { NumberParam, useQueryParam } from 'use-query-params';
import { IAffiliateTransaction } from '../types';
import { formatCurrency, formatDateTime } from '../helpers';
import { MainTitle } from '../components/MainTitle/MainTitle';
import Error from '../components/Error';

const CommissionsPage = () => {
  const limit = 10;
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [withdrawalPage, setWithdrawalPage] = useQueryParam('withdrawal-page', NumberParam);
  const offset = Number(page) > 0 ? Number(page) * limit - limit : 0;
  const withdrawalOffset = Number(withdrawalPage) > 0 ? Number(withdrawalPage) * limit - limit : 0;

  const affiliateWalletQuery = useQuery('affiliate-wallet-with-transactions', getAffiliateWalletWithTransactions);
  const {
    data: walletData,
    isLoading: isWalletLoading,
    isError: isWalletError,
    refetch: refetchWallet,
  } = affiliateWalletQuery;

  const commissionBalanceQuery = useQuery('commission-balance', getCommissionBalance);
  const {
    data: commissionBalanceData,
    isLoading: isCommissionBalanceLoading,
    isError: isCommissionBalanceError,
  } = commissionBalanceQuery;

  const walletId = walletData?.wallet?.id;

  const commissionTransactions: IAffiliateTransaction[] = (walletData?.transactions || []).filter(
    (tx) => tx.type === 'COMMISSION_PAYOUT'
  );

  const withdrawalTransactions: IAffiliateTransaction[] = (walletData?.transactions || []).filter(
    (tx) => tx.type === 'WITHDRAWAL' || tx.type === 'CORRECTION'
  );

  const hasPendingWithdrawal = withdrawalTransactions.some((tx) => tx.status === 'pending');

  const paginatedCommissionTransactions = commissionTransactions.slice(offset, offset + limit);
  const paginatedWithdrawalTransactions = withdrawalTransactions.slice(withdrawalOffset, withdrawalOffset + limit);
  const commissionPagesCount = Math.ceil(commissionTransactions.length / limit) || 1;
  const withdrawalPagesCount = Math.ceil(withdrawalTransactions.length / limit) || 1;

  const unpaidBalance = commissionBalanceData?.unpaidBalance ?? 0;

  if (isWalletError || isCommissionBalanceError) {
    return (
      <Error
        onRetry={() => {
          refetchWallet();
          commissionBalanceQuery.refetch();
        }}
      />
    );
  }

  const commissionTableFields = useMemo(
    () => [
      { key: 'id', label: _t('commission.id'), sorter: false },
      { key: 'referral', label: _t('commission.referral'), sorter: false },
      { key: 'commissionType', label: _t('commission.commission-type'), sorter: false },
      { key: 'actionType', label: _t('commission.action-type'), sorter: false },
      { key: 'actionDetails', label: _t('commission.action-details'), sorter: false },
      { key: 'amount', label: _t('commission.amount'), sorter: false },
      { key: 'createdAt', label: _t('commission.date'), sorter: false },
    ],
    []
  );

  const withdrawalTableFields = useMemo(
    () => [
      { key: 'id', label: _t('commission.id'), sorter: false },
      { key: 'type', label: _t('withdrawals-table.fields.type'), sorter: false },
      { key: 'status', label: _t('withdrawals-table.fields.status'), sorter: false },
      { key: 'amount', label: _t('commission.amount'), sorter: false },
      { key: 'createdAt', label: _t('commission.date'), sorter: false },
    ],
    []
  );

  const commissionScopedSlots = useMemo(
    () => ({
      id: ({ id }: IAffiliateTransaction) => <td>{id}</td>,
      referral: ({ affiliateCommission }: IAffiliateTransaction) => (
        <td>
          {affiliateCommission?.referralName ? (
            <CLink href={`/referrals/${affiliateCommission.referralId}`}>{affiliateCommission.referralName}</CLink>
          ) : (
            '-'
          )}
        </td>
      ),
      commissionType: ({ affiliateCommission }: IAffiliateTransaction) => (
        <td>{affiliateCommission?.commissionType || '-'}</td>
      ),
      actionType: ({ affiliateCommission }: IAffiliateTransaction) => <td>{affiliateCommission?.actionType || '-'}</td>,
      actionDetails: ({ affiliateCommission }: IAffiliateTransaction) => (
        <td>{affiliateCommission?.actionDetails || '-'}</td>
      ),
      amount: ({ amount, currency }: IAffiliateTransaction) => <td>{formatCurrency(amount, currency)}</td>,
      createdAt: ({ createdAt }: IAffiliateTransaction) => (
        <td className="text-nowrap align-middle">{formatDateTime(createdAt)}</td>
      ),
    }),
    []
  );

  const withdrawalScopedSlots = useMemo(
    () => ({
      id: ({ id }: IAffiliateTransaction) => <td>{id}</td>,
      amount: ({ amount }: IAffiliateTransaction) => <td>{formatCurrency(amount)}</td>,
      status: ({ status }: IAffiliateTransaction) => <td>{status}</td>,
      type: ({ type }: IAffiliateTransaction) => <td>{type}</td>,
      createdAt: ({ createdAt }: IAffiliateTransaction) => (
        <td className="text-nowrap align-middle">{formatDateTime(createdAt)}</td>
      ),
    }),
    []
  );

  const onCommissionPageChanged = (page: number) => {
    setPage(page, 'replaceIn');
  };

  const onWithdrawalPageChanged = (page: number) => {
    setWithdrawalPage(page, 'replaceIn');
  };

  return (
    <CContainer fluid className="c-main main-holder">
      <CRow>
        <CCol>
          <MainTitle>{_t('sidebar.commissions')}</MainTitle>
          <CCallout color="primary" className="bg-white">
            <small className="text-value-lg">{_t('payments.title')}</small>
            <div>
              <>
                {_t('payments.earning-description')}
                <br />
                {_t('payments.transfer-description')}
                <hr />
              </>
            </div>
            <div className="d-flex align-items-center justify-content-between w-100">
              <h5>
                <span>
                  {_t('commission.payout-amount')}:&nbsp;
                  <b>{formatCurrency(unpaidBalance)}</b>
                </span>
              </h5>
              <div className="d-flex flex-column align-items-end">
                <CButton
                  color="primary"
                  disabled={hasPendingWithdrawal}
                  className={hasPendingWithdrawal ? 'opacity-50' : ''}
                  style={{ cursor: hasPendingWithdrawal ? 'not-allowed' : 'pointer' }}
                  onClick={() => {
                    if (!hasPendingWithdrawal) {
                      const cashierNextUrl = process.env.REACT_APP_CRM_ROOT;
                      window.open(`${cashierNextUrl}/payments/withdrawal/${walletId}`, '_blank');
                    }
                  }}
                >
                  {_t('commission.withdraw-earnings')}
                </CButton>
                {hasPendingWithdrawal && (
                  <small className="mt-2">
                    <span style={{ marginRight: '5px' }}>⚠️</span>
                    Your withdrawal request is processing
                  </small>
                )}
              </div>
            </div>
          </CCallout>
        </CCol>
      </CRow>

      <CCard className="mb-4">
        <CCardHeader className="pb-0">
          <CCol className="d-flex align-items-center">
            <CCardTitle className="flex-grow-1">{_t('commission.earnings')}</CCardTitle>
          </CCol>
        </CCardHeader>
        <CCardBody>
          <PaginationTable
            onPageChanged={onCommissionPageChanged}
            tableFields={commissionTableFields}
            scopedSlots={commissionScopedSlots}
            pagination
            pages={commissionPagesCount}
            data={paginatedCommissionTransactions}
            loading={isWalletLoading}
            activePage={page || 1}
          />
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader className="pb-0">
          <CCol className="d-flex align-items-center">
            <CCardTitle className="flex-grow-1">{_t('withdrawals-table.title')}</CCardTitle>
          </CCol>
        </CCardHeader>
        <CCardBody>
          <PaginationTable
            onPageChanged={onWithdrawalPageChanged}
            tableFields={withdrawalTableFields}
            scopedSlots={withdrawalScopedSlots}
            pagination
            pages={withdrawalPagesCount}
            data={paginatedWithdrawalTransactions}
            loading={isWalletLoading}
            activePage={withdrawalPage || 1}
          />
        </CCardBody>
      </CCard>
    </CContainer>
  );
};

export default CommissionsPage;
